import React, { useEffect, useMemo, useState } from 'react'
import { LOADING_SCREEN_CARD_URLS } from '../constants/loadingScreenAssets'

/** Shared with the preload manifest so the gate warms exactly what this renders. */
const CARD_URLS = [...LOADING_SCREEN_CARD_URLS]

/** How long each image holds before the next one cross-fades in. */
const SHUFFLE_MS = 5000

/** Random order so every visit opens on a different image; cycles without repeating until all are shown. */
function shuffle(urls: string[]): string[] {
  const out = [...urls]
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[out[i], out[j]] = [out[j], out[i]]
  }
  return out
}

/** Resolves once fetched **and** decoded — these URLs are not in the startup manifest, so swapping to a cold one would fade in a half-painted frame. */
function warm(src: string): Promise<void> {
  const img = new Image()
  img.src = src
  const decoded = typeof img.decode === 'function' ? img.decode() : Promise.resolve()
  return decoded.catch(() => undefined)
}

/**
 * One full-bleed image behind the loading screen, shuffling to the next every {@link SHUFFLE_MS}.
 *
 * Two fixed layers ping-pong so the DOM order never changes (reordering would cancel the fade):
 * layer B is the only one whose opacity moves — it fades **in** over A, then **out** to reveal A.
 * The next image is always written into whichever layer is currently covered, so the src swap is invisible.
 */
export const LoadingScreenBackdrop: React.FC = () => {
  const order = useMemo(() => shuffle(CARD_URLS), [])
  const [aSrc, setASrc] = useState(order[0])
  const [bSrc, setBSrc] = useState(order[0])
  const [top, setTop] = useState<'a' | 'b'>('a')

  useEffect(() => {
    if (order.length < 2) return
    let cancelled = false
    let step = 0
    let showing: 'a' | 'b' = 'a'
    let timer = 0

    const tick = async () => {
      const next = order[(step + 1) % order.length]
      await warm(next)
      if (cancelled) return
      step += 1
      if (showing === 'a') {
        setBSrc(next)
        setTop('b')
        showing = 'b'
      } else {
        setASrc(next)
        setTop('a')
        showing = 'a'
      }
      timer = window.setTimeout(tick, SHUFFLE_MS)
    }

    timer = window.setTimeout(tick, SHUFFLE_MS)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [order])

  return (
    <div className="game-loading-backdrop" aria-hidden>
      <img src={aSrc} alt="" className="game-loading-backdrop-img" decoding="async" draggable={false} />
      <img
        src={bSrc}
        alt=""
        className="game-loading-backdrop-img game-loading-backdrop-img--fade"
        style={{ opacity: top === 'b' ? 1 : 0 }}
        decoding="async"
        draggable={false}
      />
      <div className="game-loading-backdrop-scrim" />
    </div>
  )
}

export default LoadingScreenBackdrop
